import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Check, ChevronDown, RotateCcw } from "lucide-react";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { create } from "zustand";

import { api } from "../api/client";
import type { DeployedUnit, Deployment, Phase } from "../api/types";
import { Checkbox } from "../components/Checkbox";
import { Spinner } from "../components/EmptyState";
import { Motion, Presence, useClosing } from "../components/presence";
import { SearchField, shortcut } from "../components/ui";
import { useDismiss } from "../components/useDismiss";
import { unitDesignation } from "../i18n/designation";
import { t } from "../i18n/i18n";
import { unitMissionName } from "../i18n/vocabulary";
import type { DossierWrite } from "../map/dossierWrites";
import { useManoeuvreWrites } from "../map/manoeuvre/useManoeuvreWrites";
import { missionFile } from "../map/units/missionImages";
import { useDeployment } from "../map/units/useDeployment";
import { orderActions, withOrders } from "../map/orderSync";
import { useUi } from "../state/ui";
import {
  catalogueItems,
  MENU_WIDTH,
  foldText,
  initialReposition,
  isRecoUnit,
  missionHint,
  missionChoiceNotes,
  missionInitials,
  noMissionBlocked,
  placement,
  readRepositionPreference,
  recoFirst,
  sameMission,
  writeRepositionPreference,
} from "./missionMenuModel";

/** Menu de mission d'une unité à un temps (docs/14 §2.5) : popover ouvert depuis la fiche d'unité, la liste des
 *  missions, le panneau Manœuvre ou le menu de l'unité sur la carte. Un seul à la fois : la demande vit dans
 *  `useMissionMenu`, le menu est rendu une fois par l'application (`MissionMenuHost`), au-dessus de tout. */

export interface MissionMenuAnchor {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface MissionMenuRequest {
  unit: string;
  phase: Phase;
  anchor: MissionMenuAnchor;
}

interface MissionMenuState {
  request: MissionMenuRequest | null;
  /** Dernière fermeture : le clic sur le chevron qui vient de fermer le menu (clic extérieur) ne le rouvre pas. */
  closed: { key: string; at: number } | null;
  open: (request: MissionMenuRequest) => void;
  close: () => void;
}

const keyOf = (unit: string, phase: Phase) => `${unit}|${phase}`;

export const useMissionMenu = create<MissionMenuState>()((set, get) => ({
  request: null,
  closed: null,
  open: (request) => set({ request }),
  close: () => {
    const request = get().request;
    if (request) set({ request: null, closed: { key: keyOf(request.unit, request.phase), at: Date.now() } });
  },
}));

/** Rectangle d'un élément, ou point (menu de la carte). */
export function anchorOf(target: Element | { x: number; y: number }): MissionMenuAnchor {
  if ("getBoundingClientRect" in target) {
    const r = target.getBoundingClientRect();
    return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  }
  return { left: target.x, top: target.y, right: target.x, bottom: target.y };
}

/** Ouvre (ou referme) le menu de mission. Dossier non enregistré : l'enregistrement est désigné (Données
 *  initiales, « Enregistrer » qui clignote) et la vue revient ici une fois le dossier enregistré (§2.11). */
export function requestMissionMenu(unit: Pick<DeployedUnit, "key">, phase: Phase, anchor: MissionMenuAnchor): void {
  const ui = useUi.getState();
  if (!ui.deploymentParams?.exercise_id) {
    ui.requestSave();
    ui.notify({ text: t("Enregistrez le dossier pour changer la mission."), tone: "info" });
    return;
  }
  const state = useMissionMenu.getState();
  const key = keyOf(unit.key, phase);
  if (state.request && keyOf(state.request.unit, state.request.phase) === key) {
    state.close();
    return;
  }
  if (state.closed?.key === key && Date.now() - state.closed.at < 300) return;
  state.open({ unit: unit.key, phase, anchor });
}

/** Chevron « Changer la mission » d'une ligne de mission (fiche, listes) : bouton frère de la zone « aller au
 *  temps ». Absent quand la mission n'est pas modifiable (CENTAC, GTIA d'appui de brigade, unité hors ODB,
 *  population) ou que l'unité n'est pas sur la carte à ce temps. */
export function MissionChevron({ unit, phase, className }: { unit: DeployedUnit; phase: Phase; className?: string }) {
  const canEdit = useUi((s) => Boolean(s.deploymentParams?.exercise_id));
  const open = useMissionMenu((s) => s.request?.unit === unit.key && s.request.phase === phase);
  if (!unit.mission_editable || !unit.positions[phase]) return null;
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-disabled={canEdit ? undefined : true}
      aria-label={t("Changer la mission en {phase}", { phase })}
      title={canEdit ? t("Changer la mission en {phase}", { phase }) : t("Enregistrez le dossier pour changer la mission.")}
      onClick={(e) => {
        e.stopPropagation();
        requestMissionMenu(unit, phase, anchorOf(e.currentTarget));
      }}
      className={clsx(
        // 20 px visibles (ligne de 32 px : min-h-8 py-1.5), zone d'appui portée à 32 px par le pseudo-élément ;
        // survol plus soutenu que celui de la ligne qui le porte.
        "relative inline-flex size-5 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors before:absolute before:-inset-1.5 before:content-['']",
        open ? "bg-line text-ink" : "hover:bg-line hover:text-ink",
        !canEdit && "cursor-default opacity-40 hover:bg-transparent",
        className,
      )}
    >
      <ChevronDown size={14} className={clsx("transition-transform duration-(--gx-enter)", open && "rotate-180")} />
    </button>
  );
}

/** Miniature d'une mission : image de l'iPad, sinon pastille neutre aux initiales du verbe. */
export function MissionThumb({ image, label, className }: { image: string | null; label: string; className?: string }) {
  return (
    <span className={clsx("flex h-5 w-7 shrink-0 items-center justify-center", className)} aria-hidden>
      {image ? (
        <img src={missionFile(image)} alt="" className="max-h-5 max-w-7 object-contain" draggable={false} />
      ) : (
        <span className="inline-flex h-4 min-w-5 items-center justify-center rounded-md border border-line-strong bg-canvas px-0.5 text-caps leading-none font-semibold text-ink-soft">
          {missionInitials(label)}
        </span>
      )}
    </span>
  );
}

/** Miniature de la mission d'une unité à un temps (catalogue du déploiement). */
export function missionImageOf(deployment: Deployment | undefined, unit: DeployedUnit, mission: string): string | null {
  if (!mission) return null;
  const side = unit.side === "ENI" ? "ENI" : "AMI";
  return deployment?.mission_catalogue?.[side]?.find((entry) => sameMission(entry.code, mission))?.image ?? null;
}

// --- « Rétablir le texte généré » --------------------------------------------------------------------------------

/** Paragraphes du §3.b que le choix d'une mission met à jour (`MISSION_FIELDS` de routes/deployment.py). */
const MISSION_FIELDS = ["sgtiaUnitMissions", "brigadeUnitMissions", "divisionMatrixText"];

/** §3.b modifié à la main : chaque paragraphe des missions revient à sa rédaction générée (`orders.reset-field`,
 *  la même action que « Rétablir le texte généré » de l'éditeur d'ordre), ce qui le retire des modifications. */
export async function restoreGeneratedText(queryClient: QueryClient, exerciseId: string, numbers: readonly number[]) {
  const notify = useUi.getState().notify;
  try {
    for (const number of numbers) {
      const order = await api.order(exerciseId, number);
      for (const field of MISSION_FIELDS.filter((f) => order.edited.includes(f))) {
        await api.resetOrderField(exerciseId, field, number);
      }
    }
    queryClient.setQueriesData<Deployment>({ queryKey: ["deployment"] }, (d) => (d ? { ...d, missions_edited: [] } : d));
    void queryClient.invalidateQueries({ queryKey: ["order", exerciseId] });
    void queryClient.invalidateQueries({ queryKey: ["order-layout", exerciseId] });
    notify({ text: t("Texte généré du §3.b rétabli"), detail: t("Les missions choisies y figurent."), tone: "ok" });
  } catch (error) {
    notify({ text: t("Texte non rétabli"), detail: error instanceof Error ? error.message : undefined, tone: "warn" });
  }
}

// --- Menu ---------------------------------------------------------------------------------------------------------


/** Pointeur grossier (doigt) : le filtre n'est pas focalisé à l'ouverture, le clavier virtuel ne couvre pas la
 *  liste et ne ferme pas le menu. */
function coarsePointer(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
}

/** Rendu unique du menu de mission (App). */
export function MissionMenuHost() {
  const request = useMissionMenu((s) => s.request);
  return createPortal(
    <Presence show={Boolean(request)}>
      {request && <MissionMenu key={keyOf(request.unit, request.phase)} request={request} />}
    </Presence>,
    document.body,
  );
}

type Item =
  | { id: "doctrinal"; mission: null; label: string; image: string | null; disabled: false; hint: null }
  | { id: string; mission: string; label: string; image: string | null; disabled: boolean; hint: string | null };

function MissionMenu({ request }: { request: MissionMenuRequest }) {
  const close = useMissionMenu((s) => s.close);
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const exerciseId = useUi((s) => s.deploymentParams?.exercise_id ?? null);
  const { data: deployment } = useDeployment();
  const { setMission } = useManoeuvreWrites();
  const unit = deployment?.units.find((u) => u.key === request.unit);
  const position = unit?.positions[request.phase];

  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [pending, setPending] = useState<string | null>(null);
  const [reposition, setReposition] = useState(() => initialReposition(position, readRepositionPreference()));
  const [where] = useState(() => placement(request.anchor, window.innerWidth, window.innerHeight));
  const closing = useClosing();
  useDismiss(!closing, close, ref);

  // Filtre focalisé à l'ouverture (au doigt : le menu, pour ne pas lever le clavier virtuel) ; à la fermeture,
  // le focus revient au contrôle qui a ouvert le menu.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (coarsePointer() ? ref.current : inputRef.current)?.focus({ preventScroll: true });
    return () => {
      const focus = document.activeElement;
      if (opener?.isConnected && (!focus || focus === document.body || ref.current?.contains(focus))) {
        opener.focus({ preventScroll: true });
      }
    };
  }, []);

  // Unité retirée, plus sur la carte à ce temps, dossier changé : le menu se ferme.
  const lost = Boolean(deployment) && (!unit || !position || !unit.mission_editable || !exerciseId);
  useEffect(() => {
    if (lost) close();
  }, [lost, close]);

  // Fenêtre élargie ou rétrécie, panneau qui défile : le menu, posé en coordonnées d'écran, se ferme. Un
  // changement de hauteur seule (clavier virtuel) le laisse ouvert.
  useEffect(() => {
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    let width = window.innerWidth;
    const onResize = () => {
      if (window.innerWidth !== width) close();
      width = window.innerWidth;
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [close]);

  const side = unit?.side === "ENI" ? "ENI" : "AMI";
  const items = useMemo<Item[]>(() => {
    if (!unit || !position) return [];
    const needle = foldText(query);
    const doctrinal = position.doctrinal_mission ?? "";
    const doctrinalLabel = doctrinal ? unitMissionName(doctrinal) : t("Sans mission");
    const out: Item[] = [];
    if (!needle || foldText(`${doctrinalLabel} ${t("Mission doctrinale")}`).includes(needle)) {
      out.push({
        id: "doctrinal",
        mission: null,
        label: doctrinalLabel,
        image: missionImageOf(deployment, unit, doctrinal),
        disabled: false,
        hint: null,
      });
    }
    const listed = catalogueItems(deployment?.mission_catalogue?.[side], query);
    for (const item of isRecoUnit(unit) ? recoFirst(listed) : listed) {
      const hint = missionHint(item.code, unit.side, unit.tactical_role);
      out.push({ id: item.code, mission: item.code, label: item.label, image: item.image, disabled: false, hint });
    }
    const none = t("Sans mission");
    if (!needle || foldText(none).includes(needle)) {
      out.push({ id: "", mission: "", label: none, image: null, disabled: noMissionBlocked(unit, request.phase), hint: null });
    }
    return out;
  }, [unit, position, query, deployment, side, request.phase]);

  useEffect(() => {
    setActive((a) => Math.min(Math.max(a, 0), Math.max(items.length - 1, 0)));
  }, [items.length]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);
  // Filtre associé à la liste (le champ de recherche partagé ne transmet pas ces attributs) : liste pilotée et
  // entrée active, lues par les lecteurs d'écran.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.setAttribute("aria-controls", "mission-menu-list");
    if (items[active]) input.setAttribute("aria-activedescendant", `mission-menu-${active}`);
    else input.removeAttribute("aria-activedescendant");
  });

  if (!unit || !position) return null;
  const name = unitDesignation(unit.display_name || unit.designation);
  const header = t("Mission en {phase} · {unit}", { phase: request.phase, unit: name });
  const current = position.mission;

  const choose = (item: Item) => {
    if (pending !== null) return;
    if (item.disabled) {
      notify({
        text: t("L'unité ne serait plus affichée"),
        detail: t("Une unité amie sans aucune mission n'est plus dessinée sur la carte : gardez une mission à l'un des temps."),
        tone: "warn",
      });
      return;
    }
    const repositionWanted = item.mission ? reposition.checked : false;
    // Rien ne change : mission doctrinale déjà en vigueur, ou même mission et même placement.
    const unchanged =
      item.mission === null
        ? !position.overridden
        : position.overridden && sameMission(current, item.mission) && repositionWanted === position.anchored;
    if (unchanged) {
      close();
      return;
    }
    setPending(item.id);
    setMission
      .mutateAsync({ unit: unit.key, phase: request.phase, mission: item.mission, reposition: repositionWanted })
      .then((write) => {
        const edited = write.result.missions_edited ?? [];
        if (edited.length && exerciseId) notifyEdited(write, item, repositionWanted, edited, exerciseId);
        // Menu fermé pendant l'attente, puis rouvert pour une autre unité ou un autre temps : il reste ouvert.
        if (useMissionMenu.getState().request === request) close();
      })
      .catch(() => setPending(null));
  };

  // §3.b modifié à la main : le choix n'atteint plus le texte ; l'avis propose d'y revenir (⌘Z annule le choix).
  const notifyEdited = (write: DossierWrite, item: Item, repositionWanted: boolean, edited: number[], dossier: string) => {
    const designation = unitDesignation(write.result.units.find((u) => u.key === unit.key)?.designation ?? unit.designation);
    const mission =
      item.mission === null
        ? position.doctrinal_mission
          ? t("mission doctrinale ({mission})", { mission: unitMissionName(position.doctrinal_mission) })
          : t("mission doctrinale")
        : item.mission === ""
          ? t("Sans mission")
          : unitMissionName(item.mission);
    notify({
      text: t("Mission de {unit} en {phase} : {mission}", { unit: designation, phase: request.phase, mission }),
      detail: withOrders(
        [
          missionChoiceNotes(write, { unit: unit.key, phase: request.phase, mission: item.mission, reposition: repositionWanted }),
          t("Le §3.b a été modifié à la main : le choix n'y parvient pas. {touche} annule le choix.", { touche: shortcut("Z") }),
        ]
          .filter(Boolean)
          .join(" "),
        write.result,
      ),
      tone: "warn",
      action: { label: t("Rétablir le texte généré"), run: () => void restoreGeneratedText(queryClient, dossier, edited) },
      // Autres paragraphes de l'OPO mis à jour par ce choix : ils restent atteignables.
      secondary: orderActions(undefined, write.result).action,
    });
  };

  const step = (from: number, dir: 1 | -1) => {
    for (let k = 1; k <= items.length; k++) {
      const i = (from + dir * k + items.length) % items.length;
      if (!items[i]!.disabled) return i;
    }
    return from;
  };
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (items.length) setActive((a) => step(a, e.key === "ArrowDown" ? 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[active];
      if (item) choose(item);
    }
  };

  const toggleReposition = (checked: boolean) => {
    setReposition({ checked, kept: !checked && position.moved });
    writeRepositionPreference(checked);
  };

  const separatorBefore = (i: number) => i > 0 && (items[i]!.id === "" || items[i - 1]!.id === "doctrinal");

  return (
    <Motion
      ref={ref}
      origin="zoom"
      role="dialog"
      aria-label={header}
      tabIndex={-1}
      onKeyDown={(e) => {
        // Au doigt, le focus est sur le menu : ↑ ↓ ⏎ y fonctionnent aussi (le filtre traite les siens).
        if (e.target === e.currentTarget) onKeyDown(e);
      }}
      className="fixed z-[100] flex flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-float outline-none"
      style={{
        left: where.left,
        width: MENU_WIDTH,
        maxHeight: where.maxHeight,
        transformOrigin: where.origin,
        ...(where.top !== undefined ? { top: where.top } : { bottom: where.bottom }),
      }}
    >
      <div className="flex min-h-9 shrink-0 items-center px-3 py-2">
        <span className="min-w-0 flex-1 break-words text-ui leading-snug font-semibold text-ink">
          {header}
        </span>
      </div>
      <div className="shrink-0 px-2 pb-2">
        <SearchField
          inputRef={inputRef}
          value={query}
          onChange={(value) => {
            setQuery(value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={t("Filtrer les missions")}
        />
        {isRecoUnit(unit) && (
          <p className="mt-1.5 px-0.5 text-meta leading-snug text-ink-muted">
            {t("Unité en reconnaissance : sa mission est RECONNAÎTRE à chaque temps ; une autre mission est relevée par la vérification de cohérence.")}
          </p>
        )}
        {unit.side === "ENI" && (
          <p className="mt-1.5 px-0.5 text-meta leading-snug text-ink-muted">
            {t("Effet sur la carte, la DIREX et les exports ; le texte de l'ennemi suit le mode d'action ennemi.")}
          </p>
        )}
      </div>
      <div
        ref={listRef}
        id="mission-menu-list"
        role="menu"
        aria-label={t("Missions de l'échelon")}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-line px-1 py-1"
      >
        {items.length === 0 && (
          <p className="px-2 py-2 text-ui text-ink-muted">{t("Aucune mission ne correspond.")}</p>
        )}
        {items.map((item, i) => {
          const doctrinal = item.id === "doctrinal";
          const checked = doctrinal ? false : item.mission === "" ? current === "" : Boolean(current) && sameMission(current, item.mission);
          const waiting = pending === item.id;
          const dash = item.mission === "" || (doctrinal && !position.doctrinal_mission);
          return (
            <div key={item.id || "none"}>
              {separatorBefore(i) && <div role="separator" className="mx-1 my-1 border-t border-line" />}
              <button
                type="button"
                id={`mission-menu-${i}`}
                data-index={i}
                role="menuitemradio"
                // Un seul élément coché : la ligne doctrinale tant que la mission n'est pas choisie à la main.
                aria-checked={doctrinal ? !position.overridden : checked && position.overridden}
                aria-disabled={item.disabled || undefined}
                tabIndex={-1}
                onPointerEnter={() => !item.disabled && setActive(i)}
                onClick={() => choose(item)}
                title={
                  item.disabled
                    ? t("L'unité ne serait plus affichée")
                    : doctrinal && !position.overridden
                      ? t("Mission en vigueur")
                      : undefined
                }
                className={clsx(
                  "flex w-full items-center gap-2 rounded-md px-2 text-left transition-colors",
                  doctrinal ? "min-h-10 py-1.5" : item.hint ? "min-h-9 py-1" : "min-h-8 py-1",
                  i === active && !item.disabled && "bg-hover",
                  item.disabled && "cursor-default opacity-40",
                )}
              >
                {dash ? (
                  <span className="flex h-5 w-7 shrink-0 items-center justify-center" aria-hidden>
                    <span className="h-px w-3.5 bg-ink-muted" />
                  </span>
                ) : (
                  <MissionThumb image={item.image} label={item.label} />
                )}
                <span className={clsx("min-w-0 flex-1 transition-opacity duration-(--gx-enter)", waiting && "opacity-40")}>
                  {doctrinal && (
                    <span className="block text-meta leading-tight text-ink-muted">{t("Mission doctrinale (selon le rôle)")}</span>
                  )}
                  <span className={clsx("block break-words text-ui leading-snug text-ink", (checked || (doctrinal && !position.overridden)) && "font-medium")}>
                    {item.label}
                  </span>
                  {item.hint && <span className="block text-meta leading-tight text-ink-muted">{t(item.hint)}</span>}
                </span>
                {waiting ? (
                  <Spinner decorative />
                ) : doctrinal ? (
                  <RotateCcw size={14} className={clsx("shrink-0", position.overridden ? "text-ink-soft" : "text-ink-muted/60")} />
                ) : (
                  <Check size={14} className={clsx("shrink-0 text-accent", !checked && "invisible")} />
                )}
              </button>
            </div>
          );
        })}
      </div>
      <p className="shrink-0 px-3 pt-1 pb-2 text-meta leading-snug text-ink-muted">
        {t("Si le 1er échelon change de posture, les autres unités, l'ennemi et l'ordre s'y accordent ; vos choix restent.")}
      </p>
      <div className="shrink-0 border-t border-line px-3 py-2.5">
        <Checkbox
          checked={reposition.checked}
          onChange={toggleReposition}
          label={t("Placer l'unité selon la mission")}
          detail={
            reposition.kept && !reposition.checked ? (
              <Motion origin="fade">{t("position choisie à la main conservée")}</Motion>
            ) : undefined
          }
        />
      </div>
    </Motion>
  );
}
